import fileDownload from "js-file-download";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/Button";
import ComboBox from "../../../components/ComboBox";
import ComboBoxStation from "../../../components/ComboBoxStation";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { Input, Select, Textarea } from "../../../components/Inputs";
import Loading from "../../../components/Loading";
import Modal from "../../../components/Modal/Modal";
import ModalCertificate from "../../../components/Modal/ModalCertificate";
import ModalConfirmation from "../../../components/Modal/ModalConfirmation";
import Pill from "../../../components/Pill";
import TitleValue from "../../../components/TitleValue";
import { certificateStatuses, resourceTypes } from "../../../constants/selects";
import { monthsAndResourceTypes } from "../../../constants/translations";
import { API_EXPORT, API_PUT } from "../../../helpers/api";
import { convertAmountToFrontend } from "../../../helpers/convert";
import { setNotification } from "../../../helpers/notification";
import {
  getActiveCertificatesRequest,
  setCertificateFilterExportEndDate,
  setCertificateFilterExportStartDate,
  setCertificateFilterProductionPeriod,
  setCertificateFilterResourceType,
  setCertificateFilterStation,
} from "../../../redux/actions/certificates";

export default function ActiveCertificates({ t }) {
  const dispatch = useDispatch();

  const { activeList: certificates, totalPageCount } = useSelector(
    (state) => state.certificates
  );

  const loading = useSelector((state) => state.certificates.loading);
  const selectedOrganization = useSelector(
    (state) => state.organizations.selectedOrganization
  );
  // Filters
  const filterStation = useSelector(
    (state) => state.certificates.filterStation
  );

  const filterResourceType = useSelector(
    (state) => state.certificates.filterResourceType
  );
  const filterProductionPeriodMonth = useSelector(
    (state) => state.certificates.filterProductionPeriodMonth
  );
  const filterProductionPeriodYear = useSelector(
    (state) => state.certificates.filterProductionPeriodYear
  );

  const filterExportStartDateMonth = useSelector(
    (state) => state.certificates.filterExportStartDateMonth
  );
  const filterExportStartDateYear = useSelector(
    (state) => state.certificates.filterExportStartDateYear
  );

  const documentCount = useSelector(
    (state) => state.certificates.documentCount
  );
  const blockedDocumentCount = useSelector(
    (state) => state.certificates.blockedDocumentCount
  );

  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const [activePage, setActivePage] = useState(0);

  const [editedCertificate, setEditedCertificate] = useState({});
  const [editedCertificateChanges, setEditedCertificateChanges] = useState({});
  const [editModal, setEditModal] = useState(false);
  const [editCertificateConfirm, setEditCertificateConfirm] = useState(false);
  const [inspectedCertificateId, setInspectedCertificateId] = useState();
  const [filterStatus, setFilterStatus] = useState(["ACTIVE", "BLOCK"]);
  const [exportLoading, setExportLoading] = useState(false);
  const [downloadFilter, setDownloadFilter] = useState({
    organizationId: "",
    stationId: "",
    resourceType: "",
    month: "",
    year: "",
    certificateStatus: ["ACTIVE", "BLOCK"],
  });
  const [editRequestLoading, setEditRequestLoading] = useState(false);

  const translations = monthsAndResourceTypes(t);
  const selectResourceType = resourceTypes(t);
  const selectStatus = certificateStatuses(t);

  const activeCertificatesColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.stationName"),
      selector: "stationName",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.resourceType"),
      selector: "resourceType",
      sortable: true,
      cell: (row) => {
        return translations[row.resourceType];
      },
    },
    {
      name: t("pages.default.certificates.table.productionPeriod"),
      selector: "productionPeriod",
      sortable: true,
      cell: (row) => {
        return `${translations[row.productionMonth]}/${row.productionYear}`;
      },
    },
    {
      name: t("pages.default.certificates.table.exportPeriod"),
      selector: "exportDate",
      sortable: true,
      cell: (row) => moment(row.exportDate).format("DD-MM-YYYY HH:mm:ss"),
    },
    {
      name: t("pages.default.certificates.table.durationOfUse"),
      selector: "durationOfUse",
      sortable: true,
    },
    {
      name: t("pages.default.certificates.table.documentCount"),
      selector: "amount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: t("pages.default.certificates.table.blockedAmount"),
      selector: "blockedAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.blockedAmount);
      },
    },
    {
      name: t("pages.default.certificates.table.status"),
      selector: "certificateStatus",
      sortable: true,
      style: { overflow: "visible" },
      cell: (row) => {
        return (
          <Pill
            label={translations[row.certificateStatus]}
            type={row.certificateStatus === "ACTIVE" ? "success" : "important"}
            tooltip={row.certificateStatus === "ACTIVE" ? "" : row.description}
          />
        );
      },
    },
    {
      name: t("pages.default.certificates.table.process"),
      selector: "process",
      sortable: false,
      width: "140px",
      cell: (row) => {
        return (
          <div style={{ display: "flex" }}>
            <Button
              title={t("ui.default.review")}
              suffixIcon="search"
              type="secondary"
              onClick={() => inspectCertificate(row)}
            />
            {row.certificateStatus === "ACTIVE" ? (
              <Button
                title={t("ui.default.edit")}
                suffixIcon="edit"
                type="primary"
                onClick={() => editCertificate(row)}
              />
            ) : null}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    filterCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterChange = (e) => {
    let splitted = [];
    let payload = {};
    splitted = e.target.value.split("-");
    payload = {
      month: splitted[1] || "",
      year: splitted[0] || "",
    };
    switch (e.target.name) {
      case "exportStartDate":
        dispatch(setCertificateFilterExportStartDate(payload));
        break;
      case "exportEndDate":
        dispatch(setCertificateFilterExportEndDate(payload));
        break;
      case "productionPeriod":
        dispatch(setCertificateFilterProductionPeriod(payload));
        break;
      case "resourceType":
        dispatch(setCertificateFilterResourceType(e.target.value));
        break;
      case "filterStatus":
        setFilterStatus(e.target.value);
        break;
      default:
        break;
    }
  };
  const selectStation = (station) => {
    dispatch(setCertificateFilterStation(station));
  };

  const inspectCertificate = (row) => {
    setInspectedCertificateId(row.id);
  };

  const editCertificate = (row) => {
    setEditedCertificate(row);
    setEditedCertificateChanges({
      amount: null,
      certificateStatus: "-",
      maxAmount: row.amount,
      description: "",
    });
    setEditModal(true);
  };

  const editCertificateChange = (e) => {
    setEditedCertificateChanges({
      ...editedCertificateChanges,
      [e.target.name]: e.target.value,
    });
  };
  const editCertificateModalToggle = () => {
    setEditModal(!editModal);
    setEditedCertificateChanges({});
    setEditedCertificate({});
  };
  const editCertificateApprove = () => {
    if (editedCertificateChanges.certificateStatus === "-") {
      setNotification(
        "error",
        t("pages.default.certificates.errors.selectStatus")
      );
      return false;
    }
    if (!editedCertificateChanges.amount) {
      setNotification(
        "error",
        t("pages.default.certificates.errors.enterValidAmount")
      );
      return false;
    }
    if (
      !editedCertificateChanges.description &&
      editedCertificateChanges.certificateStatus === "CANCEL"
    ) {
      setNotification(
        "error",
        t("pages.default.certificates.errors.enterValidDescription")
      );
      return false;
    }
    if (
      editedCertificateChanges.description.length < 10 &&
      editedCertificateChanges.certificateStatus === "CANCEL"
    ) {
      setNotification(
        "error",
        t("pages.default.certificates.errors.enterValidDescriptionLength")
      );
      return false;
    }
    setEditCertificateConfirm(true);
  };

  const editCertificateRequest = async () => {
    setEditCertificateConfirm(false);
    setEditModal(false);
    setEditRequestLoading(true);
    try {
      await API_PUT(`certificate/${editedCertificate.id}`, {
        certificateStatus: editedCertificateChanges.certificateStatus,
        amount: editedCertificateChanges.amount,
        description:
          editedCertificateChanges.certificateStatus === "CANCEL"
            ? editedCertificateChanges.description
            : "",
      });
      setNotification("success", t("pages.default.certificates.success"));
      setEditedCertificateChanges({});
      setEditedCertificate({});
      filterCertificates();
      setEditRequestLoading(false);
    } catch (error) {
      setEditRequestLoading(false);
      setNotification("error", error.response.data.message);
    }
  };

  const filterCertificates = () => {
    let params = {
      organizationId:
        selectedOrganization && selectedOrganization.organizationId
          ? selectedOrganization.organizationId
          : "",
      stationId: filterStation ? filterStation.stationId : "",
      resourceType: filterResourceType !== "-" ? filterResourceType : "",
      month: filterProductionPeriodMonth,
      year: filterProductionPeriodYear,
      exportStartDateMonth: filterExportStartDateMonth,
      exportStartDateYear: filterExportStartDateYear,
      certificateStatus: filterStatus,
    };
    dispatch(
      getActiveCertificatesRequest({
        ...params,
      })
    );
    setDownloadFilter({
      ...params,
      lang: window.localStorage.i18nextLng,
    });
  };

  const downloadCertificates = async () => {
    setExportLoading(true);
    try {
      const res = await API_EXPORT("certificate/export/excel", {
        ...downloadFilter,
      });
      const [, filename] =
        res?.headers["content-disposition"].split("filename=");
      fileDownload(res.data, filename);
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };
  const handleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);
    let params = {
      organizationId:
        selectedOrganization && selectedOrganization.organizationId
          ? selectedOrganization.organizationId
          : "",
      stationId: filterStation ? filterStation.stationId : "",
      resourceType: filterResourceType !== "-" ? filterResourceType : "",
      month: filterProductionPeriodMonth,
      year: filterProductionPeriodYear,
      exportStartDateMonth: filterExportStartDateMonth,

      exportStartDateYear: filterExportStartDateYear,

      certificateStatus: filterStatus,
      page: activePage,
      selector,
      direction,
    };
    dispatch(
      getActiveCertificatesRequest({
        ...params,
      })
    );
  };
  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.certificates.activeTitle")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--certificates-filter">
          <ComboBox t={t} />
          <ComboBoxStation
            t={t}
            selectedStation={filterStation}
            selectStation={selectStation}
          />
          <Input
            type="month"
            placement="bottom-end"
            placeholder={t(
              "pages.default.certificates.filters.allProductionPeriod"
            )}
            name="productionPeriod"
            value={
              filterProductionPeriodYear
                ? `${filterProductionPeriodYear}-${filterProductionPeriodMonth}`
                : ""
            }
            onChange={filterChange}
          />
          <Input
            type="month"
            placeholder={t(
              "pages.default.certificates.filters.exportStartDate"
            )}
            name="exportStartDate"
            placement="bottom-end"
            value={
              filterExportStartDateYear
                ? `${filterExportStartDateYear}-${filterExportStartDateMonth}`
                : ""
            }
            onChange={filterChange}
          />
          <Select
            options={selectResourceType}
            name="resourceType"
            defaultValue={filterResourceType}
            onChange={filterChange}
          />
          <Select
            options={selectStatus}
            name="filterStatus"
            defaultValue={filterStatus}
            onChange={filterChange}
          />
          <Button
            type="primary"
            label={t("ui.default.filter")}
            onClick={filterCertificates}
            id="c-filter"
          />
          <Button
            type="secondary"
            label={t("ui.default.download")}
            suffixIcon="download"
            onClick={downloadCertificates}
            disabled={exportLoading}
            id="c-download"
          />
        </div>
        {certificates.length > 0 && !loading && (
          <DataTableServerSideRendering
            columns={activeCertificatesColumns}
            data={certificates}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={totalPageCount}
            handleFetchData={handleClick}
            subTotals={[
              {
                totalCount: documentCount.totalCount,
                countInPage: documentCount.countInPage,
                sumIndex: 6,
              },
              {
                totalCount: blockedDocumentCount.totalCount,
                countInPage: blockedDocumentCount.countInPage,
                sumIndex: 7,
              },
            ]}
          />
        )}
        {certificates.length === 0 && !loading && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.certificates.activeNotFound")}
          />
        )}
        {loading && (
          <Loading text={t("pages.default.certificates.activeLoading")} />
        )}
      </div>

      {inspectedCertificateId && (
        <ModalCertificate
          t={t}
          certificateId={inspectedCertificateId}
          isOpen={inspectedCertificateId ? true : false}
          toggle={() => setInspectedCertificateId()}
          difCalculate={true}
        />
      )}
      {editedCertificate.id && (
        <Modal isOpen={editModal} toggle={editCertificateModalToggle}>
          <div className="km__p--certificate-edit-header">
            {t("pages.default.certificates.editModal.title")}
          </div>
          <div className="km__p--certificate-edit-body">
            <TitleValue
              title={t("pages.default.certificates.table.organizationName")}
              value={editedCertificate.organizationName}
              size="lg"
            />
            <TitleValue
              title={t("pages.default.certificates.table.stationName")}
              value={editedCertificate.stationName}
              size="lg"
            />
            <TitleValue
              title={t("pages.default.certificates.table.resourceType")}
              value={translations[editedCertificate.resourceType]}
              size="lg"
            />
            <Select
              label={t("pages.default.certificates.table.processType")}
              name="certificateStatus"
              defaultValue={editedCertificateChanges.certificateStatus}
              options={[
                { name: "", value: "-", disabled: true },
                { name: translations.CANCEL, value: "CANCEL" },
                { name: translations.REPEAL, value: "REPEAL" },
              ]}
              onChange={editCertificateChange}
            />
            <Input
              label={t("pages.default.certificates.table.quantity")}
              type="number"
              name="amount"
              min={1}
              defaultValue={editedCertificateChanges.amount}
              max={editedCertificateChanges.maxAmount}
              placeholder={editedCertificateChanges.maxAmount}
              onChange={editCertificateChange}
            />
            <Textarea
              label={t("pages.default.certificates.table.description")}
              name="description"
              value={
                editedCertificateChanges.certificateStatus === "CANCEL"
                  ? editedCertificateChanges.description
                  : ""
              }
              maxLength="255"
              disabled={editedCertificateChanges.certificateStatus === "REPEAL"}
              onChange={editCertificateChange}
            />
          </div>
          <div className="km__p--certificate-edit-footer">
            <Button
              label={t("pages.default.certificates.table.dismiss")}
              type="secondary"
              onClick={editCertificateModalToggle}
            />
            <Button
              label={t("pages.default.certificates.table.approve")}
              type="primary"
              onClick={() => editCertificateApprove()}
            />
          </div>
        </Modal>
      )}
      <ModalConfirmation
        isOpen={editCertificateConfirm}
        toggle={() => setEditCertificateConfirm(!editCertificateConfirm)}
        header={t("pages.default.certificates.confirmHeader")}
        body={t("pages.default.certificates.confirmBody", {
          status: translations[editedCertificateChanges.certificateStatus]
            ? translations[editedCertificateChanges.certificateStatus]
            : "",
        })}
        approve={editCertificateRequest}
        loading={editRequestLoading}
      />
    </>
  );
}
